import { Injectable } from '@angular/core'
import { Case } from '@ftr/annotations-contracts'
import { generateUuid } from '@ftr/contracts/type/shared'
import { Uuid } from '@ftr/contracts/type/shared/Uuid'
import { unwrapData } from '@ftr/foundation'
import { Action, Selector, State, StateContext, createSelector } from '@ngxs/store'
import { patch } from '@ngxs/store/operators'
import { ɵPatchSpec } from '@ngxs/store/operators/patch'
import { memoize } from 'lodash-es'
import { firstValueFrom } from 'rxjs'
import { CasesService } from '../../services'
import { ClearActiveCaseAction, FetchActiveCaseAction } from './cases.actions'
import { CasesInstanceStateModel, CasesStateModel } from './cases.model'

export function defaultCasesState(): CasesStateModel {
  return {
    casesInstanceState: {},
  }
}

export function defaultCaseInstanceState(): CasesInstanceStateModel {
  return {
    activeCase: {
      result: undefined,
      request: undefined,
    },
  }
}

@State<CasesStateModel>({
  name: 'casesState',
  defaults: defaultCasesState(),
})
@Injectable()
export class CasesState {
  constructor(private readonly casesService: CasesService) {}

  @Selector()
  static allCasesInstanceStates(state: CasesStateModel): CasesStateModel['casesInstanceState'] {
    return state.casesInstanceState
  }

  static readonly casesInstanceState = memoize(
    (
      courtSystemId: Uuid,
    ): ((sourceStates: ReturnType<typeof CasesState.allCasesInstanceStates>) => CasesInstanceStateModel) => {
      return createSelector(
        [CasesState.allCasesInstanceStates],
        (sourceStates: ReturnType<typeof CasesState.allCasesInstanceStates>) =>
          sourceStates[courtSystemId] ?? defaultCaseInstanceState(),
      )
    },
  )

  @Action(FetchActiveCaseAction)
  async fetchActiveCase(
    { setState, getState }: StateContext<CasesStateModel>,
    { courtSystemId, caseId }: FetchActiveCaseAction,
  ): Promise<void> {
    const requestId = generateUuid()
    const originalState = getState().casesInstanceState[courtSystemId] ?? defaultCaseInstanceState()

    // Don't re-fetch the active case if there is already a result or request in flight
    const existingFetch = originalState.activeCase?.request
    if (existingFetch && existingFetch.caseId === caseId && existingFetch.state !== 'error') {
      return
    }

    patchCasesInstanceState(setState, courtSystemId, {
      ...originalState,
      activeCase: {
        result: undefined,
        request: { caseId, requestId, state: 'loading' },
      },
    })

    runWithoutWaitingForActionToEnd(async () => {
      let caseResult: Case
      try {
        caseResult = await firstValueFrom(this.casesService.getCase(caseId, courtSystemId).pipe(unwrapData()))
      } catch (error) {
        const currentRequestId = getState().casesInstanceState[courtSystemId]?.activeCase?.request?.requestId
        // If a different active case has been requested in the meantime, we just ignore this result
        if (requestId !== currentRequestId) {
          return
        }

        patchCasesInstanceState(setState, courtSystemId, {
          activeCase: {
            result: undefined,
            request: { caseId, requestId, state: 'error', error },
          },
        })
        return
      }

      const currentRequestId = getState().casesInstanceState[courtSystemId]?.activeCase?.request?.requestId
      // If a different active case has been requested in the meantime, we just ignore this result
      if (requestId !== currentRequestId) {
        return
      }

      patchCasesInstanceState(setState, courtSystemId, {
        activeCase: {
          result: caseResult,
          request: { requestId, caseId, state: 'success' },
        },
      })
    })
  }

  @Action(ClearActiveCaseAction)
  clearActiveCase(
    { setState, getState }: StateContext<CasesStateModel>,
    { courtSystemId }: ClearActiveCaseAction,
  ): void {
    const originalState = getState().casesInstanceState[courtSystemId] ?? defaultCaseInstanceState()

    patchCasesInstanceState(setState, courtSystemId, {
      ...originalState,
      activeCase: {
        result: undefined,
        request: undefined,
      },
    })
  }
}

function patchCasesInstanceState(
  setState: StateContext<CasesStateModel>['setState'],
  courtSystemId: Uuid,
  update: ɵPatchSpec<CasesInstanceStateModel>,
): void {
  setState(
    patch<CasesStateModel>({
      casesInstanceState: patch({
        [courtSystemId]: patch(update),
      }),
    }),
  )
}

function runWithoutWaitingForActionToEnd(callback: () => Promise<void>): void {
  void callback()
}
